/** Shared helpers for competitor backlink analysis (workflow + standalone page). */

export function extractDomain(url: string): string {
  if (!url) return '';
  try {
    return url
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  } catch {
    return '';
  }
}

export function normalizeBacklinkUrl(url: string): string {
  if (!url) return '';
  const raw = url.trim();
  if (!raw) return '';

  try {
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = new URL(withProtocol);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/+$/, '');
    return `${host}${path}`;
  } catch {
    return raw
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/[?#].*$/, '')
      .replace(/\/+$/, '');
  }
}

export function normalizeBacklinkDomain(url: string): string {
  const key = normalizeBacklinkUrl(url);
  if (!key) return '';
  return key.split('/')[0];
}

export function canonicalReferringPageKey(url: string): string {
  const t = (url || '').trim();
  if (!t) return '';
  try {
    const withProto = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    const u = new URL(withProto);
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    const path = (u.pathname || '').replace(/\/+$/, '') || '';
    const search = u.search || '';
    return `${host}${path}${search}`;
  } catch {
    return t
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('#')[0]
      .replace(/\/+$/, '');
  }
}

export function competitorDomainsFromUrls(urls: string[]): string[] {
  const seen = new Set<string>();
  const domains: string[] = [];
  for (const raw of urls) {
    const domain = extractDomain(raw);
    if (domain && !seen.has(domain)) {
      seen.add(domain);
      domains.push(domain);
    }
  }
  return domains;
}

export function ensureHttpUrl(url: string): string {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** Accept domain or full URL; empty → ''; invalid → null. Domain-only gets https:// */
export function normalizeProjectWebsiteUrl(raw: string): string | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(ensureHttpUrl(trimmed));
    const host = (parsed.hostname || '').toLowerCase();
    if (!host) return null;
    if (host !== 'localhost') {
      if (!host.includes('.')) return null;
      if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) return null;
      if (!/^[a-z0-9.-]+$/i.test(host) && !/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
    }
    // Prefer stable stored form: origin + pathname (drop trailing slash on origin-only)
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.protocol}//${parsed.host}${path}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

